import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { openDeletionChildrenRealignPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getRouteAfterRemovingTaskAndObjectiveDetails } from 'constants/helper';
import { sendOKRDeletedEvent, sendRealignedKREvent, sendRealignedObjectiveEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { OKR_TYPES } from '../PrimaryPanel/Sections/Overview/ParentOKR';
import { QUERY_KEYS } from '../datahooks';
import { useDeleteOKRMutation } from '../datahooks/useDeleteOKRMutation';
import { useOKRDrawerControlCallbacks } from '../useOKRDrawerControlCallbacks';

/* currently only works when bottom drawer is open */
export const useOKRDeletionModalLogic = ({
  parentUuid = null,
  parentText = '',
  parentType = OKR_TYPES.OBJECTIVE,
  parentVisibilityType = 'PRIVATE',
  okrTimePeriod,
  okrUuid,
  okrType,
  okrText,
  objectiveLevel,
  okrVisibilityType,
  okrHasNoChildren,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const toast = useToast();
  const page = useViewPageName();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const isObjective = okrType === OKR_TYPES.OBJECTIVE;

  const { onDelete } = useOKRDrawerControlCallbacks();

  const { mutate, isMutating } = useDeleteOKRMutation(okrUuid);

  const handleCloseDeletionModal = () => {
    setIsDeletionModalOpen(false);
  };

  const handleOpenDeletionModal = () => {
    setIsDeletionModalOpen(true);
  };

  const handleRealignChildOKRs = () => {
    dispatch(
      openDeletionChildrenRealignPanel({
        parentUuid,
        parentText,
        parentType,
        parentVisibilityType,
        okrTimePeriod,
        okrUuid,
        okrType,
        okrText,
        objectiveLevel,
        okrVisibilityType,
        okrHasNoChildren,
      })
    );

    handleCloseDeletionModal();
  };

  const handleDeleteChildOKRs = () => {
    const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
    const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid];
    const okrSupportedByQueryKey = [QUERY_KEYS.OKR_CHILDREN, okrUuid];

    const closeDrawer = () => {
      const route = getRouteAfterRemovingTaskAndObjectiveDetails({ location: history.location });
      history.push(route);
    };

    mutate(
      {},
      {
        onError: (err) => {
          toast({
            title:
              err.message ||
              `The ${
                isObjective
                  ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                  : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
              } could not be archived`,
            status: TOAST_STATUSES.ERROR,
            position: 'bottom-left',
          });
        },
        onSuccess: () => {
          queryClient.removeQueries(REACT_QUERY_KEYS.okrTreeView);
          queryClient.removeQueries(okrQueryKey);
          queryClient.removeQueries(alignmentQueryKey);
          queryClient.removeQueries(okrSupportedByQueryKey);

          sendOKRDeletedEvent({
            eventProperties: {
              goalId: okrUuid,
              type: okrType === OKR_TYPES.OBJECTIVE ? OKR_TYPES.OBJECTIVE : OKR_TYPES.KEY_RESULT,
              source: 'Kebab Menu',
              page,
              status: 'archive',
            },
          });
          if (okrType === OKR_TYPES.OBJECTIVE) {
            sendRealignedObjectiveEvent({
              eventProperties: {
                goalId: okrUuid,
                type: 'Deletion',
                source: 'Kebab Menu',
                page,
              },
            });
          } else {
            sendRealignedKREvent({
              eventProperties: {
                goalId: okrUuid,
                type: 'Deletion',
                source: 'Kebab Menu',
                page,
              },
            });
          }

          if (typeof onDelete === 'function') {
            console.log('inside onDelete');
            onDelete(parentUuid, okrUuid);
          }
          toast({
            title: `The ${
              isObjective
                ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
            } has been archived successfully`,
            status: TOAST_STATUSES.SUCCESS,
            position: 'bottom-left',
          });

          handleCloseDeletionModal();
          closeDrawer();
        },
      }
    );
  };

  return {
    isDeletionModalOpen,
    handleOpenDeletionModal,
    handleCloseDeletionModal,
    handleDeleteChildOKRs,
    handleRealignChildOKRs,
  };
};
